import { createSelector } from '@reduxjs/toolkit';
import LoadingStatus from 'enums/loadingStatus';
import DrawerMode from 'layout/enums/drawerMode';
import { DATA_SLICE_KEY } from './dataSlice';
import { deriveActiveChip, countLotsByChip } from '../services/mapChipService';
import { resolveLotDestination, LOT_ROUTABLE_VIEWS } from '../services/lotRoutingService';
import compareBySoldHomeAddress from '../services/soldHomeSortService';
import deriveFloorPlanLotStatus from '../services/floorPlanLotStatusService';
import LotNavigationAction from '../enums/lotNavigationAction';
import LotStatus from '../enums/lotStatus';
import {
    VIEW_FLOOR_PLAN_DETAIL,
    VIEW_HOUSING_UNIT_DETAIL,
    VIEW_SOLD_FULL_LIST,
    VIEW_LOT_MAP_SELECTION,
    VIEW_MOBILE_LOT_CARD
} from '../constants';

const selectSlice = (state) => state[DATA_SLICE_KEY];

/**
 * Views the mobile drawer renders at full size. See DRAWER_PLUGIN_SEQUENCE.md.
 */
const FULL_VIEWS = [VIEW_FLOOR_PLAN_DETAIL, VIEW_HOUSING_UNIT_DETAIL, VIEW_SOLD_FULL_LIST];
const HIDDEN_VIEWS = [VIEW_MOBILE_LOT_CARD];

/**
 * Reselect prop-selector: pulls the `sitePlanLotId` passed as the second argument to a selector
 * call (e.g. `selectLotMapSelectionViewModel(state, sitePlanLotId)`) so it participates in that
 * selector's memoization cache key. Shared by selectLotRoutingOutcome and
 * selectLotMapSelectionViewModel below.
 */
const selectSitePlanLotIdProp = (_, sitePlanLotId) => sitePlanLotId;

/**
 * Plain boolean, not derived from state — the caller (lotSelectedEpic)
 * already has it via closure from context.state.isMobile (set once in index.js).
 */
const selectIsMobileProp = (_, __, isMobile) => isMobile;

/**
 * @returns {object[]} Raw lots array from the data slice.
*/
export const selectLots = (state) => selectSlice(state).lots;

/**
 * @returns {object[]} Raw house plans array from the data slice.
 */
export const selectHousePlans = (state) => selectSlice(state).housePlans;

/**
 * @returns {object[]} Raw housing units array from the data slice.
 */
export const selectHousingUnits = (state) => selectSlice(state).housingUnits;

/**
 * @returns {object[]} Raw sold lots array from the data slice — lots with a Sold status, each
 * carrying its assigned plan's sold-home attributes. Never referenced by housePlans/housingUnits.
 */
export const selectSoldLots = (state) => selectSlice(state).soldLots;

/**
 * @returns {{ planId: string|null, selectedLotId: string|null, housingUnitId: number|null }}
 */
export const selectActiveViewContext = (state) => selectSlice(state).viewState.activeViewContext;

/**
 * @returns {string|null} The lotId of the currently selected lot, or null if none.
*/
export const selectSelectedLotId = (state) => selectActiveViewContext(state).selectedLotId;

/**
 * @returns {string|null} The planId of the currently selected floor plan, or null if none.
*/
export const selectActiveFloorPlanId = (state) => selectActiveViewContext(state).planId;

/**
 * @returns {number|null} housingUnitId of the unit currently shown in housingUnitDetail, or null.
 */
export const selectActiveHousingUnitId = (state) => selectActiveViewContext(state).housingUnitId;

/**
 * @returns {'card_click'|'other'|null} Set only by card-selection epics — distinguishes a card
 * click from a map lot click when selectedLotId also changes on card click (e.g. a Model home
 * card). 'other' is a Similar Home card click under a Sold Home Card (NHS20-8827).
 */
export const selectViewEntrySource = (state) => selectActiveViewContext(state).source ?? null;

/**
 * @returns {string} The currently active view (e.g. 'listing').
 */
export const selectActiveView = (state) => selectSlice(state).viewState.activeView;

/**
 * @returns {string} The currently selected tab value ('floorPlans' | 'quickMoveIns').
 */
export const selectSelectedTab = (state) => selectSlice(state).viewState.selectedTab;

/**
 * @returns {object[]} viewState.viewHistory — the navigateTo/navigateBack undo stack.
 */
export const selectViewHistory = (state) => selectSlice(state).viewState.viewHistory;

/**
 * @returns {{ lotStatus: string|null }}
 */
export const selectSearchParameters = (state) => selectSlice(state).searchParameters;

/**
 * @returns {string} LoadingStatus value for the lot-map-inventory fetch.
 */
export const selectFetchLotInventoryStatus = (state) => selectSlice(state).fetchLotInventoryStatus;

/**
 * Gets a floor plan by its planId.
 * @param {object} state - Redux state
 * @param {string} planId - The floor plan ID to look up
 * @returns {object|null} The floor plan object or null if not found
 */
export const selectFloorPlanById = (state, planId) => {
  if (!planId) {return null;}
  return selectHousePlans(state).find(plan => plan.planId === planId) ?? null;
};

/**
 * Gets the currently selected floor plan.
 * @param {object} state - Redux state
 * @returns {object|null} The currently selected floor plan or null if none
 */
export const selectActiveFloorPlan = createSelector(
  selectActiveFloorPlanId,
  selectHousePlans,
  (planId, housePlans) => {
    if (!planId) {return null;}
    const numericPlanId = typeof planId === 'string' ? parseInt(planId, 10) : planId;
    return housePlans.find(plan => plan.planId === numericPlanId) ?? null;
  }
);

/**
 * Gets the currently selected lot with intelligent, status-aware defaulting (NHS20-8882):
 * - If the user selected a lot from the map, show that lot — but only if it both belongs to
 *   this plan AND its status matches the chip currently being browsed. A HousePlanId can span
 *   lots of different statuses (e.g. a Model home built from the same plan as several Available
 *   lots), so "belongs to this plan" alone isn't enough to trust a stale/foreign-status
 *   selectedLotId.
 * - Otherwise (no selection, or the selection doesn't qualify), default to the first lot of the
 *   floor plan whose status matches the active chip (clicked plan card without a qualifying lot
 *   selection) — never just the first lot in plan order, which could be any status.
 * - If NO lot on this plan matches the active chip's status at all, returns null rather than
 *   falling back to plan order — surfacing a foreign-status lot here would reintroduce exactly
 *   the stale/wrong-status lot this selector exists to prevent (PR #7486 review).
 * @param {object} state - Redux state
 * @returns {object|null} The currently selected lot or null if none available/none match
 */
export const selectCurrentlySelectedLot = createSelector(
  selectActiveFloorPlan,
  selectLots,
  selectSelectedLotId,
  selectSearchParameters,
  (floorPlan, lots, selectedLotId, searchParameters) => {
    if (!floorPlan || !floorPlan.lotIds || floorPlan.lotIds.length === 0) {
      return null;
    }

    const activeChip = deriveActiveChip(searchParameters);

    // If user selected a specific lot from the map, try to use that
    if (selectedLotId) {
      const selectedLot = lots.find(lot => lot.sitePlanLotId === selectedLotId);
      // Verify it belongs to this floor plan AND matches the status being browsed
      if (selectedLot && floorPlan.lotIds.includes(selectedLot.lotId) && selectedLot.status === activeChip) {
        return selectedLot;
      }
    }

    // No selection, or the selection doesn't qualify — default to the first lot on this plan
    // matching the active chip's status. No plan-order fallback: a plan with no lot at all
    // matching the active chip's status has nothing valid to show here.
    const matchingLotId = floorPlan.lotIds.find((lotId) => (
      lots.find((lot) => lot.lotId === lotId)?.status === activeChip
    ));
    if (matchingLotId == null) {
      return null;
    }

    return lots.find(lot => lot.lotId === matchingLotId) ?? null;
  }
);

/**
 * Drawer size (mobile only) — the plugin never decides its own size, it only reflects this.
 * See DRAWER_PLUGIN_SEQUENCE.md.
 * @returns {DrawerMode}
 */
export const selectDrawerMode = createSelector(
  [selectActiveView],
  (view) => {
    if (HIDDEN_VIEWS.includes(view)) {
      return DrawerMode.Hidden;
    }
    return FULL_VIEWS.includes(view) ? DrawerMode.Full : DrawerMode.Partial;
  }
);

/**
 * The housing unit object behind the currently active housingUnitDetail view, or null when not
 * in that view / not yet resolved. Sources the layout image shown inline in `[data-panel]`.
 * @returns {object|null}
 */
export const selectActiveHousingUnit = createSelector(
  [selectHousingUnits, selectActiveHousingUnitId],
  (housingUnits, housingUnitId) => housingUnits.find((unit) => unit.specificationId === housingUnitId) ?? null
);

/**
 * Whether the lot-map-inventory fetch has settled (either way) — until then, derived data like
 * chip lot counts read as 0 across the board simply because the data hasn't arrived, not because
 * there's genuinely nothing to show. See NHS20-8054.
 *
 * @returns {boolean}
 */
export const selectIsLotInventorySettled = createSelector(
  [selectFetchLotInventoryStatus],
  (status) => status === LoadingStatus.Succeeded.toString() || status === LoadingStatus.Failed.toString()
);

/**
 * Derives the active map chip key ('available' | 'model' | 'coming-soon' | 'sold') from
 * searchParameters. There is no standalone activeChip field in the store — see
 * redux/STORE_DESIGN.md and services/mapChipService.js.
 *
 * @returns {string}
 */
export const selectActiveChip = createSelector(
  [selectSearchParameters],
  (searchParameters) => deriveActiveChip(searchParameters)
);

/**
 * Derives an O(1) lookup map from the lots array, keyed by sitePlanLotId (the string id SVG
 * lot <path> elements use, e.g. 'g1_l140' — see services/processCommunityMap.js).
 * Memoized — recomputes only when the lots reference changes.
 *
 * Input (store):  [{ sitePlanLotId: 'g0_l7', status: 'available' }, { sitePlanLotId: 'g0_l8', status: 'sold' }, ...]
 * Output:         { 'g0_l7': 'available', 'g0_l8': 'sold', ... }
 *
 * The store holds the canonical array (preserves order, supports future add/remove operations).
 * This selector converts it to an object once so consumers can do lotStatusMap[sitePlanLotId] in
 * O(1) instead of a O(n) .find() on every SVG element during applyFilter.
 * Returns statuses only — CSS class mapping belongs in the DOM component.
 */
export const selectLotStatusMap = createSelector(
  [selectLots],
  (lots) => Object.fromEntries(lots.map((lot) => [lot.sitePlanLotId, lot.status]))
);

/**
 * O(1) lookup map for lots keyed by housingUnitId.
 * @returns {Record<string, object>} Map of housingUnitId to lot object.
 */
export const selectLotsById = createSelector(
  [selectLots],
  (lots) => Object.fromEntries(lots.map((lot) => [lot.lotId, lot]))
);


/**
 * Lots whose status matches the currently active chip, keyed by both id spaces this feature
 * needs to match against: sitePlanLotId (string, e.g. 'g1_l140' — SVG lot <path> elements use
 * this, see components/lotPolygonColors.dom.js#applyFilter) and lotId (integer — housePlans'
 * Lots[] and housingUnits' LotId reference lots by this id instead, see
 * services/processLotMapInventory.js). lotId is only populated once the lot-inventory fetch has
 * enriched the lot, so lots without it yet are excluded from the lotIds set.
 *
 * @returns {{ sitePlanLotIds: Set<string>, lotIds: Set<number> }}
 */
export const selectMatchedLotIds = createSelector(
  [selectLots, selectActiveChip],
  (lots, activeChip) => {
    const matchedLots = lots.filter((lot) => lot.status === activeChip);
    return {
      sitePlanLotIds: new Set(matchedLots.map((lot) => lot.sitePlanLotId)),
      lotIds: new Set(
        matchedLots.filter((lot) => lot.lotId != null).map((lot) => lot.lotId)
      ),
    };
  }
);

/**
 * Same as selectMatchedLotIds, further narrowed to the lot(s) relevant to whichever detail view
 * is currently open — used only for map polygon coloring, so opening a plan's floorPlanDetail
 * view greys out lots that don't build that plan, opening a housing unit's housingUnitDetail
 * view greys out every lot except the one that unit is built on, and opening the lotMapSelection
 * picker for a lot (multi-plan, or — on mobile — any eligible lot previewed there first, see
 * services/lotRoutingService.js) greys out every lot except the one that picker is for, until a
 * plan gets chosen (which sets activeViewContext.planId, handing narrowing back to the plan-based
 * rule below). Doesn't touch the chip-driven counts/lists elsewhere (those keep using
 * selectMatchedLotIds directly: selectFilteredHousePlans, selectFilteredHousingUnits,
 * selectHousePlanCount, selectHousingUnitCount, selectSelectedLotHousePlanIds,
 * selectLotMapSelectionViewModel).
 *
 * planId and housingUnitId are mutually exclusive on activeViewContext (see the
 * housePlanIds/housingUnitId note on services/processCommunityMap.js, and the context shapes
 * services/lotRoutingService.js produces), so only one of the three narrowing rules ever applies
 * at a time. Passes matchedLotIds through unchanged otherwise.
 *
 * @returns {{ sitePlanLotIds: Set<string>, lotIds: Set<number> }}
 */
export const selectMapMatchedLotIds = createSelector(
    [selectMatchedLotIds, selectLots, selectActiveFloorPlanId, selectActiveHousingUnitId,
        selectActiveView, selectSelectedLotId],
    (matchedLotIds, lots, planId, housingUnitId, activeView, selectedLotId) => {
        if (planId != null || housingUnitId != null) {
            const eligibleLotIds = new Set(
                lots
                    .filter((lot) => (planId != null
                        ? lot.housePlanIds.includes(planId)
                        : lot.housingUnitId === housingUnitId))
                    .map((lot) => lot.sitePlanLotId)
            );
            return {
                sitePlanLotIds: new Set(
                    [...matchedLotIds.sitePlanLotIds].filter((id) => eligibleLotIds.has(id))
                ),
                lotIds: matchedLotIds.lotIds,
            };
        }

        if (activeView === VIEW_LOT_MAP_SELECTION && selectedLotId != null) {
            return {
                sitePlanLotIds: new Set(
                    matchedLotIds.sitePlanLotIds.has(selectedLotId) ? [selectedLotId] : []
                ),
                lotIds: matchedLotIds.lotIds,
            };
        }

        return matchedLotIds;
    }
);

/**
 * Gets the currently selected lot for the active housing unit detail view.
 * - If active housing unit has a lotId, return that lot
 * - Otherwise, fall back to the lot selected on the map if present
 * @param {object} state - Redux state
 * @returns {object|null} The housing unit's lot or null
 */
export const selectCurrentlySelectedHousingUnitLot = createSelector(
  selectActiveHousingUnit,
  selectLots,
  selectSelectedLotId,
  (housingUnit, lots, selectedLotId) => {
    if (housingUnit?.lotId != null) {
      return lots.find((lot) => lot.lotId === housingUnit.lotId) ?? null;
    }

    if (selectedLotId) {
      return lots.find((lot) => lot.sitePlanLotId === selectedLotId) ?? null;
    }

    return null;
  }
);


/**
 * Housing units matching the currently active chip, enriched with lot display label for
 * Quick Move-Ins card rendering — see NHS20-8054 — and lot status (NHS20-9017: components/
 * quickMoveInCards.dom.js reads `lotStatus` to swap the price display for "Model home" text).
 * @returns {object[]}
 */
export const selectFilteredHousingUnits = createSelector(
  [selectHousingUnits, selectLotsById, selectMatchedLotIds],
  (housingUnits, lotsById, matchedLotIds) => housingUnits
    .filter((unit) => matchedLotIds.lotIds.has(unit.lotId))
    .map((unit) => ({
      ...unit,
      lotDisplayLabel: lotsById[unit.lotId]?.displayLabel ?? null,
      lotStatus: lotsById[unit.lotId]?.status ?? null,
    }))
);

/**
 * Counts lots per chip, keyed by chip key (matches MAP_CHIPS[].key, which is also the
 * raw lot status string). A lot without an assigned Floor Plan or Spec never carries one of
 * these statuses to begin with — the API/resolveLotStatus resolves it to LotStatus.NotFound
 * instead (see services/lotStatusUtils.js) — so no additional assigned-plan/spec check is
 * needed here; matching on status is sufficient. Drives both the chip badge number and, via
 * mapChips.plugin's updateCounts/fallback, chip visibility and default-selection priority
 * (Map Chips Display Logic, NHS20-8054): a chip with nothing to show must not appear.
 *
 * Counting itself lives in services/mapChipService.js#countLotsByChip — shared with
 * dataSlice.js#viewReset, which needs the same counts to derive the post-reset chip.
 *
 * @returns {Record<string, number>}
 */
export const selectChipLotCounts = createSelector(
  [selectLots],
  countLotsByChip
);

/**
 * House plans buildable on at least one lot matching the currently active chip. Drives the
 * Floor Plans tab content — see NHS20-8054. Each plan's `lotIds` is narrowed down to just the
 * matching lots, so the "N Lots Available" card badge (components/floorPlanCards.dom.js, which
 * reads `lotIds.length`) reflects lots for the active chip instead of the plan's total lot count
 * across every status.
 *
 * Also enriched with `lotStatus` (NHS20-9017), read from the plan's first matched lot — every
 * matched lot shares the active chip's status by construction (see selectMatchedLotIds), so any
 * one of them is representative. components/floorPlanCards.dom.js reads this to swap the price
 * display for "Model home" text.
 *
 * @returns {object[]}
 */
export const selectFilteredHousePlans = createSelector(
  [selectHousePlans, selectLotsById, selectMatchedLotIds],
  (housePlans, lotsById, matchedLotIds) => housePlans
    .map((plan) => {
      const lotIds = plan.lotIds.filter((lotId) => matchedLotIds.lotIds.has(lotId));
      return {
        ...plan,
        lotIds,
        lotStatus: lotsById[lotIds[0]]?.status ?? null,
      };
    })
    .filter((plan) => plan.lotIds.length > 0)
);

/**
 * Count of house plans matching the currently active chip. Drives the Floor Plans tab badge.
 * @returns {number}
 */
export const selectHousePlanCount = createSelector(
  [selectFilteredHousePlans],
  (housePlans) => housePlans.length
);

/**
 * Count of housing units matching the currently active chip. Drives the Quick Move-Ins tab badge.
 * @returns {number}
 */
export const selectHousingUnitCount = createSelector(
  [selectFilteredHousingUnits],
  (housingUnits) => housingUnits.length
);

/**
 * Plan ids relevant to the currently selected lot, drawn from the already chip-filtered plan
 * list (selectFilteredHousePlans) rather than the lot's raw housePlanIds — keeps this in step
 * with whatever searchParameters/active chip currently narrows the data to, the same rule every
 * other display selector in this file already follows. selectLots (raw) is used only to resolve
 * the id space (sitePlanLotId string -> lotId integer), not to source the plan list itself.
 * Empty Set when nothing is selected or the lot isn't found (stale/racing state).
 *
 * @returns {Set<number>}
 */
export const selectSelectedLotHousePlanIds = createSelector(
  [selectFilteredHousePlans, selectLots, selectSelectedLotId],
  (filteredHousePlans, lots, selectedSitePlanLotId) => {
    const selectedLot = lots.find((lot) => lot.sitePlanLotId === selectedSitePlanLotId);
    if (!selectedLot) {
      return new Set();
    }
    return new Set(
      filteredHousePlans
        .filter((plan) => plan.lotIds.includes(selectedLot.lotId))
        .map((plan) => plan.planId)
    );
  }
);

/**
 * Every Sold Home Card the Sold listing (NHS20-8825/8826) can show, sorted by
 * compareBySoldHomeAddress. Sourced directly from selectSoldLots (NHS20-8757) — each sold lot
 * already carries its assigned plan's sold-home attributes (address, beds, baths, garage, sqft)
 * resolved server-side, so no join against selectHousePlans is needed. `planId` is still derived
 * from housePlanIds[0] (a sold lot is always represented as a house plan, confirmed with product)
 * so findSimilarHomes can match on it; `sitePlanLotId` carries along for
 * selectSoldHomesWithSelectedFirst below to match against.
 *
 * @returns {object[]}
 */
export const selectSoldHomes = createSelector(
  [selectSoldLots],
  (soldLots) => soldLots
    .map((lot) => ({
      sitePlanLotId: lot.sitePlanLotId,
      planId: lot.housePlanIds?.[0] ?? null,
      address: lot.soldHousePlanAddress || null,
      displayLabel: lot.displayLabel || null,
      bedrooms: lot.soldHousePlanBeds,
      bathrooms: lot.soldHousePlanBaths,
      formattedBathrooms: lot.formattedBathrooms,
      garage: lot.soldHousePlanGarage,
      squareFeet: lot.soldHousePlanSize,
      formattedSquareFeet: lot.formattedSquareFeet,
    }))
    .sort(compareBySoldHomeAddress)
);

/**
 * selectSoldHomes, with whichever sold home matches the currently selected lot moved to the
 * front (NHS20-8828: "Position the selected sold lot first in the listing sequence"). A no-op
 * when nothing is selected, or when the selected lot isn't in selectSoldHomes at all (e.g. it has
 * no housePlanIds — the list still shows the rest instead of going blank).
 *
 * @returns {object[]}
 */
export const selectSoldHomesWithSelectedFirst = createSelector(
  [selectSoldHomes, selectSelectedLotId],
  (soldHomes, selectedSitePlanLotId) => {
    const selectedIndex = soldHomes.findIndex((home) => home.sitePlanLotId === selectedSitePlanLotId);
    if (selectedIndex <= 0) {
      return soldHomes;
    }
    const selectedHome = soldHomes[selectedIndex];
    const remainingHomes = soldHomes.filter((_, index) => index !== selectedIndex);
    return [selectedHome, ...remainingHomes];
  }
);

/**
 * Lot statuses eligible as "Similar Homes Available" candidates (NHS20-8827) — Available and
 * Model only. The Sold home being viewed, Coming Soon, and Unavailable/NotFound lots never
 * qualify, regardless of which chip is currently active.
 */
const SIMILAR_HOMES_ELIGIBLE_STATUSES = new Set([
  LotStatus.Available.toString(),
  LotStatus.Model.toString(),
]);

const selectSimilarHomesEligibleLotIds = createSelector(
  [selectLots],
  (lots) => new Set(
    lots
      .filter((lot) => SIMILAR_HOMES_ELIGIBLE_STATUSES.has(lot.status) && lot.lotId != null)
      .map((lot) => lot.lotId)
  )
);

/**
 * Candidate pool for "Similar Homes Available" (NHS20-8827): every buildable Available floor
 * plan, plus every Quick Move-In/Model housing unit — normalized to one shape so
 * services/similarHomesService.js can compare them against the selected sold home without caring
 * which entity type each one came from. Deliberately NOT chip-filtered (unlike
 * selectFilteredHousePlans/selectFilteredHousingUnits) — Similar Homes shows regardless of the
 * active chip, since you only ever reach it from the Sold chip looking at other statuses.
 *
 * `lotDisplayLabel` is only populated for the housing-unit-backed candidates (each sits on
 * exactly one lot); a floor plan can be buildable on many lots, so there's no single lot to show
 * — see components/similarHomesSection.dom.js, which renders it as absent instead of guessing.
 *
 * @returns {Array<{ isFloorPlan: boolean, lotStatus: string|null, planId: number|null,
 *   specificationId: number|null, name: string|null, housePlanName: string|null, bedrooms: number|null,
 *   bathrooms: number|null, formattedBathrooms: string|null, squareFeet: number|null,
 *   formattedSquareFeet: string|null, garage: number|null, formattedPrice: string|null,
 *   lotDisplayLabel: string|null }>}
 */
export const selectSimilarHomesCandidates = createSelector(
  [selectHousePlans, selectHousingUnits, selectLotsById, selectSimilarHomesEligibleLotIds],
  (housePlans, housingUnits, lotsById, eligibleLotIds) => {
    const floorPlanCandidates = housePlans
      .filter((plan) => plan.lotIds.some((lotId) => eligibleLotIds.has(lotId)))
      .map((plan) => ({
        isFloorPlan: true,
        lotStatus: deriveFloorPlanLotStatus(plan, lotsById, eligibleLotIds),
        planId: plan.planId,
        specificationId: null,
        name: plan.name,
        housePlanName: null,
        bedrooms: plan.bedrooms,
        bathrooms: plan.bathrooms,
        formattedBathrooms: plan.formattedBathrooms,
        squareFeet: plan.squareFeet,
        formattedSquareFeet: plan.formattedSquareFeet,
        garage: plan.garage,
        formattedPrice: plan.formattedPrice,
        lotDisplayLabel: null,
      }));

    const housingUnitCandidates = housingUnits
      .filter((unit) => eligibleLotIds.has(unit.lotId))
      .map((unit) => ({
        isFloorPlan: false,
        // The real LotStatus value (Available or Model — see SIMILAR_HOMES_ELIGIBLE_STATUSES
        // above) rather than a hand-derived label — components/similarHomesSection.dom.js reads
        // this directly instead of re-deriving its own "is this a Model" check.
        lotStatus: lotsById[unit.lotId]?.status ?? null,
        planId: null,
        specificationId: unit.specificationId,
        name: unit.name,
        housePlanName: unit.housePlanName,
        bedrooms: unit.bedrooms,
        bathrooms: unit.bathrooms,
        formattedBathrooms: unit.formattedBathrooms,
        squareFeet: unit.squareFeet,
        formattedSquareFeet: unit.formattedSquareFeet,
        garage: unit.garage,
        formattedPrice: unit.formattedPrice,
        lotDisplayLabel: lotsById[unit.lotId]?.displayLabel ?? null,
      }));

    return [...floorPlanCandidates, ...housingUnitCandidates];
  }
);

/**
 * Single hub for every navigation decision a lot click can produce — `lotSelectedEpic` only
 * translates whatever this returns into the matching Redux action, it makes no routing
 * judgment of its own.
 *
 * `lotSelected` (the dataSlice reducer) unconditionally moves `activeViewContext.selectedLotId`
 * to whatever lot was just clicked, valid destination or not — only `navigateTo`/`navigateBack`
 * ever move `activeView`. Without the `navigateBack` branch below, clicking a lot with no
 * destination while a routed view is still showing a *previous*, qualifying lot would leave that
 * view on-screen, now redrawn with the new, non-qualifying lot's data (see NHS20-8166) —
 * `lotMapSelection.plugin.js` reacts to `activeView` + `selectedLotId`, not to whether the
 * current `selectedLotId` ever actually qualified for the view it's in.
 *
 * @param {object} state
 * @param {string} sitePlanLotId - the SVG click id (see libraries/mapLibrary/svgLotInteraction.js);
 *   named distinctly from lot.lotId (the integer field) because both are compared in
 *   resolveLotDestination — using the same name for both would risk matching the wrong one
 *   silently.
 * @param {boolean} isMobile - device flag; determines whether a single-result lot resolves to
 *   the lotMapSelection preview (mobile) or straight to its detail view (desktop).
 * @returns {{ type: string, view: string, context: object } | { type: string } | null} `type` is
 *   always a LotNavigationAction value (`.toString()`'d, since Redux state/actions carry plain
 *   strings, not enum instances) — `NavigateTo` (with `view`/`context`) or `NavigateBack` (alone).
 */
export const selectLotRoutingOutcome = createSelector(
  [selectLots, selectMatchedLotIds, selectActiveView, selectSitePlanLotIdProp, selectIsMobileProp],
  (lots, matchedLotIds, activeView, sitePlanLotId, isMobile) => {
    // Mobile-specific behavior: when clicking a lot while in mobile lot card, stay in the mobile lot card
    // and show the mobile lot card instead of navigating to lotMapSelection/detail view.
    // The selectedLotId is still updated via lotSelected reducer, allowing the mobile lot
    // card plugin to show/update the card. This preserves map visibility for browsing.
    if (isMobile && activeView === VIEW_MOBILE_LOT_CARD) {
      return null; // No navigation; stay in mobile lot card view, just update selectedLotId
    }

    const destination = resolveLotDestination(lots, matchedLotIds, sitePlanLotId, isMobile);
    if (destination) {
      return {
        type: LotNavigationAction.NavigateTo.toString(),
        view: destination.view,
        context: destination.context,
      };
    }

    if (LOT_ROUTABLE_VIEWS.includes(activeView)) {
      return { type: LotNavigationAction.NavigateBack.toString() };
    }

    return null;
  }
);

/**
 * View model for the lotMapSelection panel: the lot's group name and display label (e.g.
 * "Group 5", "56" → header renders "Group 5 Lot 56"), its buildable plans, plan count, and
 * lowest base price (reusing each plan's own pre-formatted `formattedPrice` rather than
 * reformatting a number here). Returns empty/null defaults when the lot isn't found
 * (stale/racing state) instead of throwing.
 *
 * @param {object} state
 * @param {string} sitePlanLotId - the SVG click id, not the integer lot.lotId (see
 *   selectLotRoutingOutcome above for why this parameter keeps its own distinct name)
 * @returns {{ lotGroupName: string|null, displayLabel: string|null, plans: object[],
 *   planCount: number, lowestFormattedPrice: string|null, housingUnit: object|null }}
 */
export const selectLotMapSelectionViewModel = createSelector(
  [selectLots, selectFilteredHousePlans, selectFilteredHousingUnits, selectSitePlanLotIdProp],
  (lots, filteredHousePlans, filteredHousingUnits, sitePlanLotId) => {
    const lot = lots.find((candidate) => candidate.sitePlanLotId === sitePlanLotId);
    if (!lot) {
      return {
        lotGroupName: null, displayLabel: null, plans: [], planCount: 0,
        lowestFormattedPrice: null, housingUnit: null,
      };
    }

    if (lot.housingUnitId != null) {
      const housingUnit = filteredHousingUnits.find((unit) => unit.lotId === lot.lotId) ?? null;
      return {
        lotGroupName: lot.lotGroupName,
        displayLabel: lot.displayLabel,
        plans: [],
        planCount: 0,
        lowestFormattedPrice: housingUnit?.formattedPrice ?? null,
        housingUnit,
      };
    }

    const plans = filteredHousePlans.filter((plan) => lot.housePlanIds.includes(plan.planId));
    const lowestPricedPlan = plans.reduce(
      (lowest, plan) => (plan.price != null && (lowest === null || plan.price < lowest.price) ? plan : lowest),
      null
    );
    return {
      lotGroupName: lot.lotGroupName,
      displayLabel: lot.displayLabel,
      plans,
      planCount: plans.length,
      lowestFormattedPrice: lowestPricedPlan?.formattedPrice ?? null,
      housingUnit: null,
    };
  }
);
